import { EventEmitter } from 'events';

import { WebClientFetchRequester } from '../requester/WebClientFetchRequester';
import { WebClientRequesterWrapper, WebClientRequester, VoidWebClientSender } from '../requester/webClientRequester';
import { GetSessionInfoMethod } from '../api/methods/GetSessionInfoMethod';
import { SessionInfoResult, SessionInfo } from '../api/types/SessionInfo';
import { getSuccessResult } from '../api/vizPortalWebClientHelpers';
import { WebClientApiResponse } from '../api/WebClientApiResponse';

enum SessionState {
  Exists,
  NoSession,
  Unknown
}

/*
Purpose of this object is to manage the current session state and provide helpers.
Can do the following:
- Fetch the session from the server
- Cache the current session
- TODO: Add a listener for changes to the session
*/

export class SessionManager extends EventEmitter {

  public static SESSION_UPDATED_EVENT: string = 'sessionUpdated';

  private fetchSession: VoidWebClientSender<WebClientApiResponse<SessionInfoResult>>;

  private sessionInfo?: SessionInfo;

  private sessionState: SessionState;
  private numSessionInfoRequests: number;
  private sessionInfoPromise: PromiseLike<SessionInfo|void>;

  public constructor(
    requester: WebClientRequester = new WebClientFetchRequester()
  ) {
    super();
    this.sessionState = SessionState.Unknown;
    this.numSessionInfoRequests = 0;
    this.sessionInfoPromise = Promise.resolve();

    this.fetchSession = WebClientRequesterWrapper.createSender(requester, GetSessionInfoMethod);
  }

  public getSession(): SessionInfo|void {
    return this.sessionInfo;
  }

  public getSessionInfoPromise(): PromiseLike<SessionInfo|void> {
    return this.sessionInfoPromise;
  }

  public waitForSession(): PromiseLike<SessionInfo|void> {
    return (this.numSessionInfoRequests > 0) ? this.sessionInfoPromise : Promise.resolve();
  }

  /**
   * Adds the request to a queue of other session update requests
   * @param makeRequest, if the promise from makeRequest successfully returns void then the session will be cleared
   * @returns {PromiseLike<void>} The promise will return void but the session can be retrieved by SessionManager::getSession()
   */
  public sessionInfoRequest(makeRequest: () => PromiseLike<SessionInfo|void>): PromiseLike<SessionInfo|void> {
    let decrementSessionInfoRequests = () => { this.numSessionInfoRequests--; };

    this.numSessionInfoRequests++;

    this.sessionInfoPromise = this.waitForSession()
      .then(makeRequest)
      .then(this.setSessionInfo.bind(this))
      .then( // Acts like finally
        decrementSessionInfoRequests,
        decrementSessionInfoRequests // Make the promise successful to keep the chain going
      )
      .then(() => this.getSession());

    return this.sessionInfoPromise;
  }

  public ensureSessionInfo(): PromiseLike<SessionInfo|void> {
    if (!this.sessionInfo && !this.numSessionInfoRequests) {
      return this.refreshSessionInfo();
    }

    return this.waitForSession();
  }

  public refreshSessionInfo(): PromiseLike<SessionInfo|void> {
    return this.sessionInfoRequest(() => {
      const fetchSuccessfulSession = () => this.fetchSession().then<SessionInfo>(getSuccessResult);

      let fetchSessionPromise = fetchSuccessfulSession();

      if (!WebClientFetchRequester.getXsrfToken()) {
        // Getting the session the first time will fail because the XSRF token is not set.
        // Use the first request to set the XSRF token cookie and the second to actually get the session.
        // This only works on desktop where the auth header is added to all requests but will not work in the browser
        // because the WebClient API clears the workgroup_session_id header when a request fails authentication.
        fetchSessionPromise = fetchSessionPromise.then((s) => s, fetchSuccessfulSession);
      }

      return fetchSessionPromise.then((s) => s, () => undefined); // Make a successful promise and clear the session
    });
  }

  private setSessionInfo(newSessionInfo?: SessionInfo): void {
    if (!newSessionInfo) {
      this.clearSessionInfo();
      return;
    }

    this.sessionInfo = newSessionInfo;
    this.sessionState = SessionState.Exists;
    this.emit(SessionManager.SESSION_UPDATED_EVENT);
  }

  private clearSessionInfo(): void {
    if (this.sessionState === SessionState.NoSession) {
      return;
    }

    this.sessionState = SessionState.NoSession;
    delete this.sessionInfo;
    this.emit(SessionManager.SESSION_UPDATED_EVENT);
  }

}
